import React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Status badge palette aligned to brand guidelines v1.1:
 *   Moss (#4A7C59)        — success / active / completed
 *   Clay (#B5603E)        — alert / error / deceased
 *   Burnt Orange (#C4622D) — warning / sold / retired-action
 *   Olive (#6B7C4A)       — info-secondary / pasture / animal-care
 *   Sky Muted (#4A90A4)   — informational / pending / vet
 *   Token Muted (#8A8A8A) — inactive / empty / unknown
 *
 * Each entry uses a 35%-opacity background (bg-X/20-style) with full-strength
 * foreground for AA contrast in light mode, and dark-mode counterparts
 * via the `dark:` prefix.
 */
const statusStyles: Record<string, string> = {
  // Success states → Moss
  active:         "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30",
  in_service:     "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30",
  processed:      "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30",

  // Warning / in-progress → Burnt Orange
  maintenance:    "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
  resting:        "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
  sold:           "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",

  // Error / alert → Clay
  out_of_service: "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30",
  deceased:       "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30",
  error:          "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30",

  // Info-secondary / land-related → Olive
  pasture:        "bg-olive/15 text-olive border-olive/30 dark:bg-olive-dark/20 dark:text-olive-dark dark:border-olive-dark/30",
  "animal-care":  "bg-olive/15 text-olive border-olive/30 dark:bg-olive-dark/20 dark:text-olive-dark dark:border-olive-dark/30",

  // Informational → Sky Muted
  pending:        "bg-sky-muted/15 text-sky-muted border-sky-muted/30 dark:bg-sky-muted-dark/20 dark:text-sky-muted-dark dark:border-sky-muted-dark/30",
  vet:            "bg-sky-muted/15 text-sky-muted border-sky-muted/30 dark:bg-sky-muted-dark/20 dark:text-sky-muted-dark dark:border-sky-muted-dark/30",

  // Inactive / unknown → Muted (token)
  inactive:       "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30",
  retired:        "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30",
  empty:          "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30",
  unknown:        "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30",
};

interface StatusBadgeProps {
  status: string | null | undefined;
  className?: string;
}
export function StatusBadge({ status, className }: StatusBadgeProps) {
  // Defensive: server-side data may not include `status` (column drift,
  // missing from a SELECT, NULL in DB). Render a neutral fallback rather
  // than crashing with `undefined.replace()`.
  const safe = typeof status === "string" && status.length > 0 ? status : "unknown";
  const style = statusStyles[safe] || statusStyles.unknown;
  const label = safe
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Badge
      variant="outline"
      className={cn(
        // Space Mono per spec — status badges are data labels.
        "font-mono text-2xs uppercase tracking-wider",
        style,
        className,
      )}
    >
      {label}
    </Badge>
  );
}